import type { AbaPainel } from './types';

interface Props {
  /** Acampamento: atalhos de equipe/campistas. */
  acampamento: boolean;
  mostrarLoja: boolean;
  vendeIngressos: boolean;
  mostrarEquipes: boolean;
  rotuloPessoas: string;
  aoIrPara: (aba: AbaPainel, extra?: Record<string, string>) => void;
  aoAbrirCaixa: () => void;
}

interface Atalho {
  chave: string;
  icone: string;
  titulo: string;
  descricao: string;
  cor: 'primary' | 'accent' | 'secondary';
  aoClicar: () => void;
}

const CORES: Record<'primary' | 'accent' | 'secondary', string> = {
  primary: 'bg-primary-100 text-primary-700',
  accent: 'bg-accent-100 text-accent-900',
  secondary: 'bg-secondary-100 text-secondary-900',
};

// Fileira de atalhos do Início: 3 a 4 cartões grandes, mudando pelo tipo de evento.
export default function AtalhosInicio({
  acampamento,
  mostrarLoja,
  vendeIngressos,
  mostrarEquipes,
  rotuloPessoas,
  aoIrPara,
  aoAbrirCaixa,
}: Props) {
  const atalhos: Atalho[] = acampamento
    ? [
        {
          chave: 'equipe',
          icone: 'ri-team-line',
          titulo: 'Equipe',
          descricao: 'Pessoas que servem',
          cor: 'primary',
          aoClicar: () => aoIrPara('equipe_acampamento', { tela: 'pessoas' }),
        },
        {
          chave: 'campistas',
          icone: 'ri-flag-2-line',
          titulo: 'Campistas',
          descricao: 'Quem vai acampar',
          cor: 'accent',
          aoClicar: () => aoIrPara('campistas', { tela: 'pessoas' }),
        },
        {
          chave: 'montagem',
          icone: 'ri-layout-grid-line',
          titulo: 'Montagem',
          descricao: 'Equipes do evento',
          cor: 'secondary',
          aoClicar: () => aoIrPara('equipe_acampamento', { tela: 'montagem' }),
        },
        {
          chave: 'relatorios',
          icone: 'ri-file-list-3-line',
          titulo: 'Relatórios',
          descricao: 'Camisetas e listas',
          cor: 'primary',
          aoClicar: () => aoIrPara('campistas', { tela: 'relatorios' }),
        },
      ]
    : mostrarLoja
      ? [
          {
            chave: 'caixa',
            icone: 'ri-cash-line',
            titulo: 'Abrir o caixa',
            descricao: 'Vender ficha e cobrar',
            cor: 'primary',
            aoClicar: aoAbrirCaixa,
          },
          {
            chave: 'vendas',
            icone: 'ri-line-chart-line',
            titulo: 'Vendas de hoje',
            descricao: 'Quanto entrou hoje',
            cor: 'accent',
            aoClicar: () => aoIrPara('dia', { tela: 'vendas' }),
          },
          {
            chave: 'barracas',
            icone: 'ri-store-2-line',
            titulo: 'Barracas',
            descricao: 'Produtos e cardápio',
            cor: 'secondary',
            aoClicar: () => aoIrPara('dia', { tela: 'barracas' }),
          },
          {
            chave: 'servindo',
            icone: 'ri-team-line',
            titulo: 'Quem está servindo',
            descricao: 'Equipe do dia',
            cor: 'primary',
            aoClicar: () => aoIrPara('dia', { tela: 'servindo' }),
          },
        ]
      : vendeIngressos
        ? [
            {
              chave: 'compradores',
              icone: 'ri-user-line',
              titulo: 'Compradores',
              descricao: 'Quem comprou ingresso',
              cor: 'primary',
              aoClicar: () => aoIrPara('pessoas'),
            },
            {
              chave: 'portaria',
              icone: 'ri-qr-scan-2-line',
              titulo: 'Portaria',
              descricao: 'Conferir ingressos',
              cor: 'accent',
              aoClicar: () => aoIrPara('portaria'),
            },
            {
              chave: 'divulgar',
              icone: 'ri-share-line',
              titulo: 'Divulgar',
              descricao: 'Link e cartaz',
              cor: 'secondary',
              aoClicar: () => aoIrPara('inicio', { divulgar: '1' }),
            },
            {
              chave: 'financeiro',
              icone: 'ri-money-dollar-circle-line',
              titulo: 'Financeiro',
              descricao: 'Quanto entrou',
              cor: 'primary',
              aoClicar: () => aoIrPara('financeiro'),
            },
          ]
        : [
          {
            chave: 'pessoas',
            icone: 'ri-group-line',
            titulo: rotuloPessoas,
            descricao: 'Quem se inscreveu',
            cor: 'primary',
            aoClicar: () => aoIrPara('pessoas'),
          },
          ...(mostrarEquipes
            ? [
                {
                  chave: 'equipes',
                  icone: 'ri-layout-grid-line',
                  titulo: 'Equipes',
                  descricao: 'Montagem do evento',
                  cor: 'accent' as const,
                  aoClicar: () => aoIrPara('equipes'),
                },
              ]
            : []),
          {
            chave: 'financeiro',
            icone: 'ri-money-dollar-circle-line',
            titulo: 'Financeiro',
            descricao: 'Entradas e despesas',
            cor: 'secondary',
            aoClicar: () => aoIrPara('financeiro'),
          },
        ];

  return (
    <div>
      <h2 className="px-1 text-[15px] font-bold text-foreground-950">Atalhos</h2>
      <div className="mt-2 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        {atalhos.slice(0, 4).map((a) => (
          <button
            key={a.chave}
            type="button"
            onClick={a.aoClicar}
            className="flex min-h-[92px] cursor-pointer flex-col items-start justify-center gap-2 rounded-3xl border border-background-200 bg-white px-3.5 py-3 text-left transition-colors hover:border-primary-200 hover:bg-primary-50/40"
          >
            <span
              className={`flex h-9 w-9 items-center justify-center rounded-xl text-lg ${CORES[a.cor]}`}
            >
              <i className={a.icone} aria-hidden />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-[14px] font-bold text-foreground-950">
                {a.titulo}
              </span>
              <span className="mt-0.5 block truncate text-[11.5px] text-foreground-600">
                {a.descricao}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}